import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

// ─── Tipos ───

export type LogTipo = 'sucesso' | 'erro' | 'aviso';

export interface LogEntry {
  linha: number;
  tipo: LogTipo;
  mensagem: string;
  campo?: string;
}

export interface ImportResult {
  total: number;
  sucesso: number;
  erros: number;
  avisos: number;
  logs: LogEntry[];
}

export interface CampoImportacao {
  /** Nome da coluna no template */
  header: string;
  /** Nome do campo no DTO */
  campo: string;
  /** Obrigatório? */
  obrigatorio: boolean;
  /** Tipo de validação */
  tipo: 'texto' | 'numero' | 'data' | 'opcao' | 'documento';
  /** Opções válidas (para tipo 'opcao') */
  opcoes?: string[];
  /** Máscara ou regex de validação */
  validacao?: RegExp;
  /** Valor padrão quando vazio */
  valorPadrao?: any;
  /** Transformação antes de salvar */
  transformar?: (valor: any) => any;
  /** Ajuste do texto ANTES de validar o tamanho (ex.: "Minas Gerais" vira "MG") */
  preparar?: (valor: string) => string;
  /** Outros nomes de coluna aceitos (ex.: os da planilha que o próprio sistema exporta) */
  sinonimos?: string[];
  /** Tamanho mínimo */
  minLength?: number;
  /** Tamanho máximo */
  maxLength?: number;
  /** Dica para o template */
  exemplo?: string;
}

// ─── Configurações de importação ───

const UF_POR_NOME: Record<string, string> = {
  'acre': 'AC', 'alagoas': 'AL', 'amapa': 'AP', 'amazonas': 'AM', 'bahia': 'BA', 'ceara': 'CE',
  'distrito federal': 'DF', 'espirito santo': 'ES', 'goias': 'GO', 'maranhao': 'MA', 'mato grosso': 'MT',
  'mato grosso do sul': 'MS', 'minas gerais': 'MG', 'para': 'PA', 'paraiba': 'PB', 'parana': 'PR',
  'pernambuco': 'PE', 'piaui': 'PI', 'rio de janeiro': 'RJ', 'rio grande do norte': 'RN',
  'rio grande do sul': 'RS', 'rondonia': 'RO', 'roraima': 'RR', 'santa catarina': 'SC',
  'sao paulo': 'SP', 'sergipe': 'SE', 'tocantins': 'TO',
};

/** "Minas Gerais" ou "mg" vira "MG"; sem correspondência, devolve como veio (o tamanho é validado depois). */
export const siglaDoEstado = (valor: string): string => {
  const texto = valor.normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
  if (texto.length === 2) return texto.toUpperCase();
  return UF_POR_NOME[texto] ?? valor;
};

/** Excel guarda CEP e CNH digitados como número e come o zero à esquerda. */
const completarComZeros = (tamanho: number) => (valor: string): string => {
  const digitos = valor.replace(/\D/g, '');
  return /^\d+$/.test(valor) && digitos.length < tamanho && digitos.length >= tamanho - 2
    ? digitos.padStart(tamanho, '0')
    : valor;
};

// Nomes de coluna iguais aos da exportação de locatários (listagem/index.tsx), para o arquivo exportado
// voltar inteiro. Os sinônimos cobrem planilhas exportadas antes de os nomes serem alinhados (DL-169).
export const CAMPOS_LOCATARIO: CampoImportacao[] = [
  { header: 'Nome*', campo: 'nome', obrigatorio: true, tipo: 'texto', exemplo: 'João da Silva' },
  { header: 'Tipo Documento (CPF/CNPJ)', campo: 'tipoDocumento', obrigatorio: false, tipo: 'opcao', opcoes: ['CPF', 'CNPJ'], exemplo: 'CPF', transformar: (v: string) => v?.toUpperCase() === 'CNPJ' ? 2 : 1 },
  { header: 'Documento* (CPF/CNPJ)', campo: 'documento', obrigatorio: true, tipo: 'documento', exemplo: '123.456.789-00' },
  { header: 'RG', campo: 'rg', obrigatorio: false, tipo: 'texto', exemplo: '12.345.678-9' },
  { header: 'Telefone', campo: 'telefone', obrigatorio: false, tipo: 'texto', exemplo: '(11) 99999-9999' },
  { header: 'Email', campo: 'email', obrigatorio: false, tipo: 'texto', exemplo: 'email@exemplo.com' },
  { header: 'Telefone Reserva', campo: 'telefoneReserva', obrigatorio: false, tipo: 'texto', exemplo: '(11) 98888-8888' },
  { header: 'Vencimento Habilitação', sinonimos: ['Venc. Habilitação', 'Vencimento CNH', 'Validade CNH'], campo: 'vencimentoHabilitacao', obrigatorio: false, tipo: 'data', exemplo: '31/12/2025' },
  { header: 'Número CNH', sinonimos: ['CNH', 'Registro CNH'], campo: 'numeroRegistroCnh', obrigatorio: false, tipo: 'texto', maxLength: 11, preparar: completarComZeros(11), exemplo: '12345678901' },
  { header: 'KM Médio/Dia', campo: 'kmMedioPorDia', obrigatorio: false, tipo: 'numero', valorPadrao: 110, exemplo: '110' },
  { header: 'CEP', campo: 'cep', obrigatorio: false, tipo: 'texto', preparar: completarComZeros(8), exemplo: '01001-000' },
  { header: 'Estado', sinonimos: ['UF'], campo: 'estado', obrigatorio: false, tipo: 'texto', maxLength: 2, preparar: siglaDoEstado, exemplo: 'SP' },
  { header: 'Cidade', campo: 'cidade', obrigatorio: false, tipo: 'texto', exemplo: 'São Paulo' },
  { header: 'Logradouro', campo: 'logradouro', obrigatorio: false, tipo: 'texto', exemplo: 'Rua das Flores' },
  { header: 'Número', campo: 'numero', obrigatorio: false, tipo: 'texto', exemplo: '123' },
  { header: 'Bairro', campo: 'bairro', obrigatorio: false, tipo: 'texto', exemplo: 'Centro' },
  { header: 'Complemento', campo: 'complemento', obrigatorio: false, tipo: 'texto', exemplo: 'Apto 101' },
];

const CORES_VALIDAS = ['Prata', 'Preto', 'Branco', 'Cinza', 'Vermelho', 'Azul', 'Verde', 'Amarelo', 'Marrom', 'Bege', 'Laranja', 'Dourado', 'Roxo', 'Rosa', 'Fantasia'];

export const CAMPOS_VEICULO: CampoImportacao[] = [
  { header: 'Placa*', campo: 'placa', obrigatorio: true, tipo: 'texto', minLength: 7, exemplo: 'ABC-1D23', transformar: (v: string) => v?.toUpperCase().replace(/[^A-Z0-9\-]/g, '') },
  { header: 'Marca*', campo: 'marca', obrigatorio: true, tipo: 'texto', exemplo: 'Toyota' },
  { header: 'Modelo*', campo: 'modelo', obrigatorio: true, tipo: 'texto', exemplo: 'Corolla' },
  { header: 'Ano*', campo: 'ano', obrigatorio: true, tipo: 'texto', exemplo: '2023' },
  { header: 'Tipo (Carro/Moto/Caminhão)', campo: 'tipoVeiculo', obrigatorio: false, tipo: 'opcao', opcoes: ['Carro', 'Moto', 'Caminhão', 'Caminhao'], exemplo: 'Carro', transformar: (v: string) => { const n = v?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''); return n === 'moto' ? 2 : n === 'caminhao' ? 4 : 1; } },
  { header: 'Cor', campo: 'cor', obrigatorio: false, tipo: 'opcao', opcoes: CORES_VALIDAS, exemplo: 'Prata' },
  { header: 'Hodômetro', campo: 'hodometro', obrigatorio: false, tipo: 'numero', exemplo: '15000' },
  { header: 'Custo', campo: 'custo', obrigatorio: false, tipo: 'numero', exemplo: '85000.00' },
  { header: 'Data Compra', campo: 'dataCompra', obrigatorio: false, tipo: 'data', exemplo: '01/01/2023' },
  { header: 'Vencimento Licenciamento', campo: 'vencimentoLicenciamento', obrigatorio: false, tipo: 'data', exemplo: '31/12/2025' },
  { header: 'Renavam', campo: 'renavam', obrigatorio: false, tipo: 'texto', exemplo: '12345678901' },
  { header: 'Chassi', campo: 'chassi', obrigatorio: false, tipo: 'texto', exemplo: '9BWZZZ377VT004251' },
];

export const CAMPOS_PECA: CampoImportacao[] = [
  { header: 'Nome*', campo: 'nome', obrigatorio: true, tipo: 'texto', exemplo: 'Filtro de Óleo' },
  { header: 'Marca*', campo: 'marca', obrigatorio: true, tipo: 'texto', exemplo: 'Bosch' },
  { header: 'Modelo*', campo: 'modelo', obrigatorio: true, tipo: 'texto', exemplo: 'F026407006' },
  { header: 'Tipo Veículo (Carro/Moto/Ambos)', campo: 'tipoVeiculo', obrigatorio: false, tipo: 'opcao', opcoes: ['Carro', 'Moto', 'Ambos'], exemplo: 'Carro', transformar: (v: string) => { const n = v?.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, ''); return n === 'moto' ? 2 : n === 'ambos' ? 3 : 1; } },
  { header: 'Vida Útil (km)', campo: 'vidaUtilEmKm', obrigatorio: false, tipo: 'numero', exemplo: '10000' },
];

export const CAMPOS_SERVICO: CampoImportacao[] = [
  { header: 'Nome*', campo: 'nome', obrigatorio: true, tipo: 'texto', exemplo: 'Troca de Óleo' },
  { header: 'Valor*', campo: 'valor', obrigatorio: true, tipo: 'numero', exemplo: '150.00' },
  { header: 'Periodicidade (km)', campo: 'periodoEmKm', obrigatorio: false, tipo: 'numero', exemplo: '10000' },
];

// ─── Funções de validação ───

function validarCPF(cpf: string): boolean {
  const numeros = cpf.replace(/\D/g, '');
  if (numeros.length !== 11 || /^(\d)\1+$/.test(numeros)) return false;
  let soma = 0;
  for (let i = 0; i < 9; i++) soma += parseInt(numeros.charAt(i)) * (10 - i);
  let resto = 11 - (soma % 11);
  if (resto >= 10) resto = 0;
  if (resto !== parseInt(numeros.charAt(9))) return false;
  soma = 0;
  for (let i = 0; i < 10; i++) soma += parseInt(numeros.charAt(i)) * (11 - i);
  resto = 11 - (soma % 11);
  if (resto >= 10) resto = 0;
  return resto === parseInt(numeros.charAt(10));
}

function validarCNPJ(cnpj: string): boolean {
  const numeros = cnpj.replace(/\D/g, '');
  if (numeros.length !== 14 || /^(\d)\1+$/.test(numeros)) return false;
  const pesos1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  const pesos2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let soma = 0;
  for (let i = 0; i < 12; i++) soma += parseInt(numeros.charAt(i)) * pesos1[i];
  let resto = soma % 11;
  if (resto < 2) resto = 0; else resto = 11 - resto;
  if (resto !== parseInt(numeros.charAt(12))) return false;
  soma = 0;
  for (let i = 0; i < 13; i++) soma += parseInt(numeros.charAt(i)) * pesos2[i];
  resto = soma % 11;
  if (resto < 2) resto = 0; else resto = 11 - resto;
  return resto === parseInt(numeros.charAt(13));
}

function parseData(valor: any): Date | null {
  if (!valor) return null;

  // Se for um número do Excel (serial date) — pode vir como number ou string numérica
  const num = typeof valor === 'number' ? valor : (typeof valor === 'string' && /^\d+$/.test(valor.trim()) ? parseFloat(valor.trim()) : NaN);
  if (!isNaN(num) && num > 0 && num < 2958466) { // 2958466 = 31/12/9999 em serial Excel
    const data = XLSX.SSF.parse_date_code(num);
    if (data) return new Date(data.y, data.m - 1, data.d);
  }

  const str = String(valor).trim();

  // DD/MM/YYYY
  const brMatch = str.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (brMatch) {
    const [, dia, mes, ano] = brMatch;
    const d = new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia));
    if (!isNaN(d.getTime())) return d;
  }

  // YYYY-MM-DD
  const isoMatch = str.match(/^(\d{4})[\/\-.](\d{1,2})[\/\-.](\d{1,2})$/);
  if (isoMatch) {
    const [, ano, mes, dia] = isoMatch;
    const d = new Date(parseInt(ano), parseInt(mes) - 1, parseInt(dia));
    if (!isNaN(d.getTime())) return d;
  }

  return null;
}

function parseNumero(valor: any): number | null {
  if (valor === null || valor === undefined || valor === '') return null;
  if (typeof valor === 'number') return valor;
  const str = String(valor).trim().replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.');
  const num = parseFloat(str);
  return isNaN(num) ? null : num;
}

// ─── Leitura do arquivo ───

export function lerArquivo(file: File): Promise<any[][]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array', cellDates: false, cellNF: false, cellText: false });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '', raw: true }) as any[][];
        resolve(json);
      } catch (err) {
        reject(new Error('Erro ao ler o arquivo. Verifique se é um CSV ou Excel válido.'));
      }
    };
    reader.onerror = () => reject(new Error('Erro ao ler o arquivo.'));
    reader.readAsArrayBuffer(file);
  });
}

// ─── Validação de uma linha ───

function validarLinha(
  rowIndex: number,
  row: Record<string, any>,
  campos: CampoImportacao[],
  logs: LogEntry[]
): Record<string, any> | null {
  const resultado: Record<string, any> = {};
  let temErro = false;

  for (const campo of campos) {
    let valor = row[campo.header];

    // Tratar valores vazios
    if (valor === undefined || valor === null || String(valor).trim() === '') {
      if (campo.obrigatorio) {
        logs.push({ linha: rowIndex, tipo: 'erro', campo: campo.header, mensagem: `Campo obrigatório "${campo.header}" está vazio.` });
        temErro = true;
        continue;
      }
      if (campo.valorPadrao !== undefined) {
        resultado[campo.campo] = campo.valorPadrao;
        logs.push({ linha: rowIndex, tipo: 'aviso', campo: campo.header, mensagem: `"${campo.header}" vazio, usando valor padrão: ${campo.valorPadrao}.` });
      }
      continue;
    }

    const valorOriginal = valor; // preservar valor original (pode ser number do Excel)
    valor = String(valor).trim();

    // Validar tipo
    switch (campo.tipo) {
      case 'texto': {
        if (campo.preparar) valor = campo.preparar(valor);
        if (campo.minLength && valor.length < campo.minLength) {
          logs.push({ linha: rowIndex, tipo: 'erro', campo: campo.header, mensagem: `"${campo.header}" deve ter pelo menos ${campo.minLength} caracteres. Valor: "${valor}".` });
          temErro = true;
          continue;
        }
        if (campo.maxLength && valor.length > campo.maxLength) {
          logs.push({ linha: rowIndex, tipo: 'aviso', campo: campo.header, mensagem: `"${campo.header}" truncado para ${campo.maxLength} caracteres.` });
          valor = valor.substring(0, campo.maxLength);
        }
        resultado[campo.campo] = campo.transformar ? campo.transformar(valor) : valor;
        break;
      }
      case 'numero': {
        const num = parseNumero(valor);
        if (num === null) {
          logs.push({ linha: rowIndex, tipo: 'erro', campo: campo.header, mensagem: `"${campo.header}" não é um número válido. Valor: "${valor}".` });
          temErro = true;
          continue;
        }
        resultado[campo.campo] = num;
        break;
      }
      case 'data': {
        const data = parseData(valorOriginal);
        if (!data) {
          logs.push({ linha: rowIndex, tipo: 'erro', campo: campo.header, mensagem: `"${campo.header}" não é uma data válida. Use DD/MM/AAAA. Valor: "${valor}".` });
          temErro = true;
          continue;
        }
        resultado[campo.campo] = data.toISOString();
        break;
      }
      case 'opcao': {
        const normalizado = valor.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const match = campo.opcoes?.find(o => o.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() === normalizado);
        if (!match) {
          logs.push({ linha: rowIndex, tipo: 'erro', campo: campo.header, mensagem: `"${campo.header}" inválido: "${valor}". Opções: ${campo.opcoes?.join(', ')}.` });
          temErro = true;
          continue;
        }
        resultado[campo.campo] = campo.transformar ? campo.transformar(match) : match;
        break;
      }
      case 'documento': {
        const numeros = valor.replace(/\D/g, '');
        // Erro estrutural (quantidade de dígitos) bloqueia a linha.
        if (numeros.length !== 11 && numeros.length !== 14) {
          logs.push({ linha: rowIndex, tipo: 'erro', campo: campo.header, mensagem: `Documento deve ter 11 (CPF) ou 14 (CNPJ) dígitos. Valor: "${valor}".` });
          temErro = true;
          continue;
        }
        // Dígito verificador é apenas AVISO (não bloqueia): o cadastro manual aceita documentos
        // sem validar o checksum, então bloquear aqui impediria importar/atualizar registros que
        // já existem no sistema com CPF/CNPJ "fora do padrão".
        if (numeros.length === 11 && !validarCPF(numeros)) {
          logs.push({ linha: rowIndex, tipo: 'aviso', campo: campo.header, mensagem: `CPF "${valor}" não passa na validação de dígito — importado mesmo assim.` });
        } else if (numeros.length === 14 && !validarCNPJ(numeros)) {
          logs.push({ linha: rowIndex, tipo: 'aviso', campo: campo.header, mensagem: `CNPJ "${valor}" não passa na validação de dígito — importado mesmo assim.` });
        }
        resultado[campo.campo] = valor;
        break;
      }
    }

    if (campo.validacao && !campo.validacao.test(String(resultado[campo.campo]))) {
      logs.push({ linha: rowIndex, tipo: 'erro', campo: campo.header, mensagem: `"${campo.header}" não atende ao formato esperado. Valor: "${valor}".` });
      temErro = true;
    }
  }

  return temErro ? null : resultado;
}

// ─── Preparar linhas (header map + filtragem) — compartilhado ───

interface LinhaMapeada {
  rowNum: number;
  row: Record<string, any>;
  /** headers (config) cujo valor bruto na planilha veio NÃO-vazio nesta linha */
  preenchidos: Set<string>;
}

const repararAcentuacao = (h: string) => {
  if (!/[ÃÂ]/.test(h)) return h;
  try { return decodeURIComponent(escape(h)); } catch { return h; }
};

const normalizarHeader = (h: string) => repararAcentuacao(h)
  .replace(/\*/g, '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();

const headerSemOpcoes = (h: string) => normalizarHeader(h).replace(/\([^)]*\)/g, '').replace(/\s+/g, ' ').trim();

export function encontrarCampoPorHeader(hRaw: string, campos: CampoImportacao[]): CampoImportacao | undefined {
  const exato = campos.find(c => normalizarHeader(c.header) === normalizarHeader(hRaw));
  if (exato) return exato;
  const porSinonimo = campos.find(c => c.sinonimos?.some(sin => normalizarHeader(sin) === normalizarHeader(hRaw)));
  if (porSinonimo) return porSinonimo;
  const base = headerSemOpcoes(hRaw);
  const candidatos = campos.filter(c => headerSemOpcoes(c.header) === base);
  return candidatos.length === 1 ? candidatos[0] : undefined;
}

function prepararDados(dados: any[][], campos: CampoImportacao[]): { linhas: LinhaMapeada[]; erro: LogEntry | null; avisos: LogEntry[] } {
  if (dados.length < 2) {
    return { linhas: [], erro: { linha: 0, tipo: 'erro', mensagem: 'O arquivo não contém dados além do cabeçalho.' }, avisos: [] };
  }

  const headersRaw = dados[0].map((h: any) => String(h).trim());
  const linhasBrutas = dados.slice(1).filter(row => row.some(cell => cell !== '' && cell !== null && cell !== undefined));

  if (linhasBrutas.length === 0) {
    return { linhas: [], erro: { linha: 0, tipo: 'erro', mensagem: 'O arquivo não contém linhas de dados.' }, avisos: [] };
  }

  const headerMap: Record<string, string> = {};
  const avisos: LogEntry[] = [];
  for (const hRaw of headersRaw) {
    if (!hRaw) continue;
    const campoMatch = encontrarCampoPorHeader(hRaw, campos);
    if (campoMatch) headerMap[hRaw] = campoMatch.header;
    else avisos.push({ linha: 1, tipo: 'aviso', campo: hRaw, mensagem: `Coluna "${hRaw}" não corresponde a nenhum campo do template e foi ignorada.` });
  }

  const headersResolvidos = Object.values(headerMap);
  const headersObrigatorios = campos.filter(c => c.obrigatorio).map(c => c.header);
  const headersFaltando = headersObrigatorios.filter(h => !headersResolvidos.includes(h));
  if (headersFaltando.length > 0) {
    return { linhas: [], erro: { linha: 0, tipo: 'erro', mensagem: `Colunas obrigatórias não encontradas: ${headersFaltando.join(', ')}. Verifique se está usando o template correto.` }, avisos };
  }

  const linhas: LinhaMapeada[] = linhasBrutas.map((rawRow, i) => {
    const row: Record<string, any> = {};
    const preenchidos = new Set<string>();
    headersRaw.forEach((hRaw: string, idx: number) => {
      const hCampo = headerMap[hRaw];
      if (hCampo) {
        row[hCampo] = rawRow[idx];
        const cell = rawRow[idx];
        if (cell !== '' && cell !== null && cell !== undefined && String(cell).trim() !== '') {
          preenchidos.add(hCampo);
        }
      }
    });
    return { rowNum: i + 2, row, preenchidos }; // +2: linha 1 = cabeçalho, índice 0-based
  });

  return { linhas, erro: null, avisos };
}

// Extrai a mensagem de erro do backend e contabiliza (já existe vs erro real).
function registrarErroEnvio(err: any, rowNum: number, rotulo: string, result: ImportResult) {
  const data = err?.response?.data;
  // LocacoesException usa Mensagem (PascalCase via Newtonsoft); model validation usa title + errors
  let msg = data?.Mensagem || data?.mensagem || '';
  if (!msg && data?.errors) {
    const detalhes = ([] as string[]).concat(...Object.values(data.errors) as string[][]).join('; ');
    msg = detalhes || data?.title || 'Erro de validação.';
  }
  if (!msg) msg = data?.title || err?.message || 'Erro desconhecido ao salvar.';

  const msgLower = msg.toLowerCase();
  const jaExiste = msgLower.includes('já possui') || msgLower.includes('ja possui')
    || msgLower.includes('já existe') || msgLower.includes('ja existe')
    || msgLower.includes('already exists') || msgLower.includes('duplicat');

  if (jaExiste) {
    result.avisos++;
    result.logs.push({ linha: rowNum, tipo: 'aviso', mensagem: `Linha ${rowNum} (${rotulo}) ignorada (já existe): ${msg}` });
  } else {
    result.erros++;
    result.logs.push({ linha: rowNum, tipo: 'erro', mensagem: `Erro ao salvar linha ${rowNum} (${rotulo}): ${msg}` });
  }
}

// ─── Processar importação (create-only, legado) ───

export async function processarImportacao(
  dados: any[][],
  campos: CampoImportacao[],
  camposFixos: Record<string, any>,
  enviar: (item: Record<string, any>) => Promise<void>,
  onProgresso?: (atual: number, total: number) => void,
): Promise<ImportResult> {
  const result: ImportResult = { total: 0, sucesso: 0, erros: 0, avisos: 0, logs: [] };

  const { linhas, erro, avisos } = prepararDados(dados, campos);
  result.logs.push(...avisos);
  result.avisos += avisos.length;
  if (erro) {
    result.logs.push(erro);
    result.erros = 1;
    return result;
  }
  result.total = linhas.length;

  for (let i = 0; i < linhas.length; i++) {
    const { rowNum, row } = linhas[i];

    const logsAntes = result.logs.length;
    const item = validarLinha(rowNum, row, campos, result.logs);
    result.avisos += result.logs.slice(logsAntes).filter(l => l.tipo === 'aviso').length;

    if (!item) {
      result.erros++;
      onProgresso?.(i + 1, linhas.length);
      continue;
    }

    const payload = { ...item, ...camposFixos };

    try {
      await enviar(payload);
      result.sucesso++;
      result.logs.push({ linha: rowNum, tipo: 'sucesso', mensagem: `Linha ${rowNum} importada com sucesso.` });
    } catch (err: any) {
      registrarErroEnvio(err, rowNum, `linha ${rowNum}`, result);
    }

    onProgresso?.(i + 1, linhas.length);
  }

  return result;
}

// ─── Upsert com detecção de divergência + aprovação ───

export interface DiffCampo {
  campo: string;
  header: string;
  de: any;
  para: any;
}

export type TipoItemPlano = 'novo' | 'divergente' | 'igual' | 'invalido';

export interface ItemPlano {
  linha: number;
  tipo: TipoItemPlano;
  rotulo: string;                              // identificação amigável (ex: nome)
  payloadCriacao?: Record<string, any>;        // p/ 'novo'
  payloadAtualizacao?: Record<string, any>;    // p/ 'divergente' (merge existente + provido)
  diffs?: DiffCampo[];
  aprovado: boolean;
}

export interface PlanoImportacao {
  itens: ItemPlano[];
  novos: number;
  divergentes: number;
  iguais: number;
  invalidos: number;
  logsValidacao: LogEntry[];
}

// Normaliza chave de match (documento → dígitos; placa → alfanumérico maiúsculo).
const chaveNormalizada = (v: any) => String(v ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

function valoresIguais(campo: CampoImportacao, atual: any, novo: any): boolean {
  if (campo.tipo === 'documento') return chaveNormalizada(atual) === chaveNormalizada(novo);
  if (campo.tipo === 'data') {
    const d = (x: any) => String(x ?? '').slice(0, 10); // compara só a data (YYYY-MM-DD)
    return d(atual) === d(novo);
  }
  if (campo.tipo === 'numero') return Number(atual) === Number(novo);
  return String(atual ?? '').trim().toLowerCase() === String(novo ?? '').trim().toLowerCase();
}

/**
 * Cruza as linhas da planilha com os registros existentes (por `campoChave`) e classifica
 * cada uma em novo / divergente / igual / invalido. Para divergentes, lista campo a campo o
 * que mudou e monta o payload de atualização mesclado — ignorando colunas vazias na planilha
 * (não apaga dados existentes).
 */
export function montarPlanoImportacao(
  dados: any[][],
  campos: CampoImportacao[],
  camposFixos: Record<string, any>,
  existentes: Record<string, any>[],
  campoChave: string,            // campo do DTO usado p/ casar (ex: 'documento', 'placa')
  rotuloCampo: string = 'nome',
): PlanoImportacao {
  const plano: PlanoImportacao = { itens: [], novos: 0, divergentes: 0, iguais: 0, invalidos: 0, logsValidacao: [] };

  const { linhas, erro, avisos } = prepararDados(dados, campos);
  plano.logsValidacao.push(...avisos);
  if (erro) {
    plano.logsValidacao.push(erro);
    return plano;
  }

  // Index dos existentes por chave normalizada
  const porChave = new Map<string, Record<string, any>>();
  for (const ex of existentes) {
    const k = chaveNormalizada(ex[campoChave]);
    if (k) porChave.set(k, ex);
  }

  for (const { rowNum, row, preenchidos } of linhas) {
    const item = validarLinha(rowNum, row, campos, plano.logsValidacao);

    if (!item) {
      plano.invalidos++;
      plano.itens.push({ linha: rowNum, tipo: 'invalido', rotulo: `Linha ${rowNum}`, aprovado: false });
      continue;
    }

    const rotulo = String(item[rotuloCampo] ?? item[campoChave] ?? `Linha ${rowNum}`);
    const chave = chaveNormalizada(item[campoChave]);
    const existente = chave ? porChave.get(chave) : undefined;

    if (!existente) {
      plano.novos++;
      plano.itens.push({ linha: rowNum, tipo: 'novo', rotulo, payloadCriacao: { ...item, ...camposFixos }, aprovado: true });
      continue;
    }

    // Considera apenas campos explicitamente preenchidos na planilha (merge — ignora vazios/defaults)
    const diffs: DiffCampo[] = [];
    const valoresProvidos: Record<string, any> = {};
    for (const campo of campos) {
      if (campo.campo === campoChave) continue;   // a chave é a identidade, nunca é alterada
      if (!preenchidos.has(campo.header)) continue;
      if (!(campo.campo in item)) continue;
      const novo = item[campo.campo];
      const atual = existente[campo.campo];
      valoresProvidos[campo.campo] = novo;
      if (!valoresIguais(campo, atual, novo)) {
        diffs.push({ campo: campo.campo, header: campo.header, de: atual, para: novo });
      }
    }

    if (diffs.length === 0) {
      plano.iguais++;
      plano.itens.push({ linha: rowNum, tipo: 'igual', rotulo, aprovado: false });
      continue;
    }

    plano.divergentes++;
    plano.itens.push({
      linha: rowNum, tipo: 'divergente', rotulo, diffs,
      payloadAtualizacao: { ...existente, ...valoresProvidos },
      aprovado: true,
    });
  }

  return plano;
}

/** Executa o plano: cria os novos aprovados e atualiza os divergentes aprovados. */
export async function processarPlano(
  plano: PlanoImportacao,
  enviar: (item: Record<string, any>) => Promise<void>,
  atualizar: (item: Record<string, any>) => Promise<void>,
  onProgresso?: (atual: number, total: number) => void,
): Promise<ImportResult> {
  const result: ImportResult = {
    total: plano.itens.length,
    sucesso: 0,
    erros: plano.invalidos,
    avisos: plano.logsValidacao.filter(l => l.tipo === 'aviso').length,
    logs: [...plano.logsValidacao],
  };

  // Contabiliza os que não serão aplicados
  for (const it of plano.itens) {
    if (it.tipo === 'igual') {
      result.avisos++;
      result.logs.push({ linha: it.linha, tipo: 'aviso', mensagem: `Linha ${it.linha} (${it.rotulo}) já está atualizada — nada a alterar.` });
    } else if (it.tipo === 'divergente' && !it.aprovado) {
      result.avisos++;
      result.logs.push({ linha: it.linha, tipo: 'aviso', mensagem: `Linha ${it.linha} (${it.rotulo}) não aprovada — mantida sem alteração.` });
    }
  }

  const aplicaveis = plano.itens.filter(it => (it.tipo === 'novo' || it.tipo === 'divergente') && it.aprovado);
  let feitos = 0;

  for (const it of aplicaveis) {
    try {
      if (it.tipo === 'novo') {
        await enviar(it.payloadCriacao!);
        result.sucesso++;
        result.logs.push({ linha: it.linha, tipo: 'sucesso', mensagem: `Linha ${it.linha} (${it.rotulo}) criada com sucesso.` });
      } else {
        await atualizar(it.payloadAtualizacao!);
        result.sucesso++;
        const campos = it.diffs?.map(d => d.header).join(', ');
        result.logs.push({ linha: it.linha, tipo: 'sucesso', mensagem: `Linha ${it.linha} (${it.rotulo}) atualizada: ${campos}.` });
      }
    } catch (err: any) {
      registrarErroEnvio(err, it.linha, it.rotulo, result);
    }
    feitos++;
    onProgresso?.(feitos, aplicaveis.length);
  }

  return result;
}

// ─── Gerar template ───

export function gerarTemplate(campos: CampoImportacao[], nomeArquivo: string) {
  const headers = campos.map(c => c.header);
  const exemplos = campos.map(c => c.exemplo || '');

  const ws = XLSX.utils.aoa_to_sheet([headers, exemplos]);

  // Definir largura das colunas
  ws['!cols'] = headers.map(h => ({ wch: Math.max(h.length + 5, 18) }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Template');
  XLSX.writeFile(wb, `${nomeArquivo}.xlsx`);
  toast.success('Template baixado com sucesso!');
}
